import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { alpha } from '@mui/material/styles';
import { useTranslation, Trans } from 'react-i18next';
import { withSnackbar } from '../../../utils/withSnackbar';
import {
  Button,
  Typography,
  Grid,
  Divider,
  Paper,
  TextField,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Collapse,
  Box,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';

import { getGlossary, createGlossaryEntry, updateGlossaryEntry, updateGlossaryEntryMode, deleteGlossaryEntry, deleteGlossaryTranslation, resetErrors } from '../../../modules/glossary.js';
import { getGlossarySettings, saveGlossarySettings } from '../../../modules/config';
import { enableLangifyFeature, disableLangifyFeature } from '../../../modules/shop';
import ItemsList from '../../../components/ItemsList';
import LoadingIndicator from '../../../components/LoadingIndicator';
import SolutionsArticleLink from '../../../components/SolutionsArticleLink.js';
import { FilterOptionsQuery, FilterOptionsSort, FilterOptionsSelect } from '../../../components/ItemsListFilterOptions';
import { UpgradePlanHint } from '../../../components/PricingPlans';

const glossaryStyles = {
  paper: (theme) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2)
  }),
  paperList: {
    padding: 0,
    marginBottom: 0,
    position: 'relative'
  },
  contentColumn: {
    margin: 0,
  },
  divider: {
    margin: '1rem 0 1rem 0'
  },
  titleContainer: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  }),
  betaBadge: (theme) => ({
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: '0.75rem',
    marginLeft: '4px'
  }),
  addButtonContainer: (theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  }),
  glossaryCount: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  }),
  createFormContainer: (theme) => ({
    padding: 0,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    overflow: 'hidden'
  }),
  createFormHeader: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
  }),
  createFormBody: (theme) => ({
    padding: theme.spacing(2.5)
  }),
  createFormFields: (theme) => ({
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  }),
  createFormArrow: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    color: theme.palette.primary.main,
    opacity: 0.6,
    mx: 0.5
  }),
  createFormActions: (theme) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  }),
  selectControl: {
    minWidth: 140
  },
  sourceField: {
    minWidth: 180,
    flex: 1,
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      bottom: '-20px',
      marginTop: 0
    }
  },
  targetField: {
    minWidth: 180,
    flex: 1,
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      bottom: '-20px',
      marginTop: 0
    }
  }
};

function Glossary(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const glossaryEntries = useSelector(state => state.glossary.glossary_entries);
  const overlimit = useSelector(state => state.glossary.overlimit);
  const editLanguage = useSelector(state => state.shop.shop.edit_language);
  const glossaryError = useSelector(state => state.glossary.error);
  const getPending = useSelector(state => state.glossary.get_glossary_pending);
  const createPending = useSelector(state => state.glossary.create_glossary_entry_pending);
  const updatePending = useSelector(state => state.glossary.update_glossary_entry_pending);
  const deletePending = useSelector(state => state.glossary.delete_glossary_entry_pending);
  const deleteTranslationPending = useSelector(state => state.glossary.delete_glossary_translation_pending);
  const exportPending = useSelector(state => state.glossary.export_glossary_pending);
  const importPending = useSelector(state => state.glossary.import_glossary_pending);
  const progress = useSelector(state => state.glossary.progress);
  const chargeName = useSelector(state => state.shop.shop.charge?.name);
  const langifyFeatures = useSelector(state => state.shop.shop.langify_features);

  // Check if glossary feature is enabled
  const glossaryFeature = langifyFeatures?.find(x => x.name === 'glossary');
  const isGlossaryEnabled = glossaryFeature?.is_enabled === true;

  // Glossary settings (case-sensitive matching)
  const glossarySettingsData = useSelector(state => state.config?.glossary_settings_data);
  const saveGlossarySettingsPending = useSelector(state => state.config?.save_glossary_settings_pending);
  const serverCaseSensitive = useMemo(() => {
    const entry = glossarySettingsData?.find(s => s?.slug === 'case_sensitive');
    const v = entry?.value;
    return v === true || v === 'true' || v === 1 || v === '1';
  }, [glossarySettingsData]);
  const [isCaseSensitive, setIsCaseSensitive] = useState(false);
  const caseSensitiveTouchedRef = useRef(false);

  const [glossaryItems, setGlossaryItems] = useState([]);

  // Create form state
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ source: '', type: 'translate', target: '' });
  const [createValidation, setCreateValidation] = useState({ source: false, sourceDuplicate: false, target: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('empty_first');
  const [translationModeFilter, setTranslationModeFilter] = useState('all');

  // ItemsList data state
  const [itemsListData, setItemsListData] = useState([]);
  const [overlimitItemsListData, setOverlimitItemsListData] = useState([]);

  // Get glossary limit based on plan
  const getGlossaryLimit = () => {
    switch (chargeName) {
      case 'basic':
        return 3;
      case 'growth':
        return 50;
      case 'premium':
        return 100;
      default:
        return 0; // Free plan or unknown
    }
  };

  const glossaryLimit = getGlossaryLimit();
  const isLimitReached = glossaryItems.length >= glossaryLimit;

  // Get the next plan above current plan
  const getNextPlan = () => {
    const plans = ['free', 'basic', 'growth', 'premium'];
    const currentPlanIndex = plans.findIndex(p => p === chargeName?.toLowerCase());
    if (currentPlanIndex === -1 || currentPlanIndex >= plans.length - 1) {
      return null; // No upgrade available (unknown plan or already on premium)
    }
    return plans[currentPlanIndex + 1];
  };

  // Get upgrade features (glossary limits for plans above current)
  const getUpgradeFeatures = () => {
    const planLimits = {
      'free': 0,
      'basic': 3,
      'growth': 50,
      'premium': 100
    };
    const plans = ['free', 'basic', 'growth', 'premium'];
    const currentPlanIndex = plans.findIndex(p => p === chargeName?.toLowerCase());

    if (currentPlanIndex === -1) return [];

    const features = [];
    for (let i = currentPlanIndex + 1; i < plans.length; i++) {
      const plan = plans[i];
      const planName = plan.charAt(0).toUpperCase() + plan.slice(1);
      features.push(t('profile.glossary.planEntries', { plan: planName, count: planLimits[plan] }));
    }
    return features;
  };

  const nextPlan = getNextPlan();
  const upgradeFeatures = getUpgradeFeatures();
  const canUpgrade = nextPlan !== null;

  // Determine loading type based on which operation is pending
  const getLoadingType = () => {
    if (createPending) return 'creating';
    if (updatePending) return 'saving';
    if (deletePending || deleteTranslationPending) return 'deleting';
    if (exportPending) return 'exporting';
    if (importPending) return 'importing';
    if (getPending) return 'loading';
    return 'loading';
  };

  useEffect(() => {
    dispatch(getGlossary(editLanguage));
  }, [dispatch, editLanguage]);

  useEffect(() => {
    dispatch(getGlossarySettings());
  }, [dispatch]);

  // Sync local toggle with server value once it arrives, unless the user has just toggled it.
  useEffect(() => {
    if (!caseSensitiveTouchedRef.current && glossarySettingsData != null) {
      setIsCaseSensitive(serverCaseSensitive);
    }
  }, [serverCaseSensitive, glossarySettingsData]);

  const handleCaseSensitiveToggle = (e) => {
    const next = e.target.checked;
    caseSensitiveTouchedRef.current = true;
    setIsCaseSensitive(next);
    dispatch(saveGlossarySettings(
      [{ namespace: 'glossary', slug: 'case_sensitive', type: 'bool', value: next }],
      (response) => {
        if (response && response.success === false) {
          // rollback
          setIsCaseSensitive(!next);
          props.enqueueSnackbar(response?.message || t('system.error'), { variant: 'error' });
        }
        dispatch(getGlossarySettings());
      }
    ));
  };


  // Watch for creation success/error
  useEffect(() => {
    const wasSubmitting = isSubmitting;
    const nowPending = createPending;

    // If we were submitting and now creation is complete (pending went from true to false)
    if (wasSubmitting && !nowPending) {
      setIsSubmitting(false);

      if (glossaryError && glossaryError.action) {
        // Error occurred - show error snackbar
        const errorMessage = glossaryError.message || t('profile.glossary.createError');
        props.enqueueSnackbar(errorMessage, {
          variant: 'error',
          persist: false
        });
      } else {
        // Success - close form
        setIsCreating(false);
        setCreateForm({ source: '', type: 'translate', target: '' });
        setCreateValidation({ source: false, target: false });
      }
    }
  }, [createPending, glossaryError]);

  useEffect(() => {
    if (glossaryEntries) {
      if (glossaryEntries.length > 0) {
        const formattedItems = glossaryEntries
          .filter(entry => entry != null)
          .map(entry => {
            const id = entry.id;
            const source = entry.text || entry.source || '';
            const type = entry.type || 'translate';

            // Get target from translations object or direct target field
            let target = '';
            if (entry.translations && editLanguage) {
              target = entry.translations[editLanguage] || '';
            } else {
              target = entry.target || '';
            }

            return {
              id,
              source,
              type,
              target,
              translationMode: type,
              planRestricted: false
            };
          })
          .reverse();
        setGlossaryItems(formattedItems);
      } else {
        // Clear glossary items when entries are empty
        setGlossaryItems([]);
      }
    }
  }, [glossaryEntries, editLanguage]);

  // Filtered and sorted glossary items (regular items only)
  const filteredAndSortedItems = useMemo(() => {
    let regularItems = [...glossaryItems];

    // Apply translation mode filter
    if (translationModeFilter && translationModeFilter !== 'all') {
      regularItems = regularItems.filter(item => {
        if (translationModeFilter === 'translate') {
          return item.type === 'translate' || item.translationMode === 'translate';
        } else if (translationModeFilter === 'block') {
          return item.type === 'block' || item.translationMode === 'block';
        }
        return true;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      regularItems = regularItems.filter(item => {
        const sourceMatch = item.source.toLowerCase().includes(query);
        const typeMatch = item.type.toLowerCase().includes(query);
        const targetMatch = item.target.toLowerCase().includes(query);
        return sourceMatch || typeMatch || targetMatch;
      });
    }

    // Apply sorting
    switch (sortOrder) {
      case 'empty_first':
        // Sort with untranslated items first, then by ID
        regularItems.sort((a, b) => {
          const aUntranslated = a.type === 'translate' && !a.target.trim();
          const bUntranslated = b.type === 'translate' && !b.target.trim();

          if (aUntranslated && !bUntranslated) return -1;
          if (!aUntranslated && bUntranslated) return 1;

          // If both same state, maintain original order (reversed ID order)
          return 0;
        });
        break;
      case 'id':
        // Sort by ID descending (newest first)
        regularItems.sort((a, b) => b.id - a.id);
        break;
      case 'alphabetical':
        // Sort alphabetically by source
        regularItems.sort((a, b) => a.source.toLowerCase().localeCompare(b.source.toLowerCase()));
        break;
      default:
        break;
    }

    return regularItems;
  }, [glossaryItems, translationModeFilter, searchQuery, sortOrder]);

  // Overlimit items (no filtering, no sorting)
  const overlimitItems = useMemo(() => {
    if (!overlimit || overlimit.length === 0) return [];

    return overlimit
      .filter(entry => entry != null)
      .map(entry => {
        const id = entry.id;
        const source = entry.text || entry.source || '';
        const type = entry.type || 'translate';

        // Get target from translations object or direct target field
        let target = '';
        if (entry.translations && editLanguage) {
          target = entry.translations[editLanguage] || '';
        } else {
          target = entry.target || '';
        }

        return {
          id,
          source,
          type,
          target,
          translationMode: type,
          planRestricted: true
        };
      })
      .reverse();
  }, [overlimit, editLanguage]);

  // Update ItemsList data whenever filtered items change (regular items)
  useEffect(() => {
    const newData = [{
      id: 'glossary',
      title: t('profile.glossary.entriesTitle'),
      fields: filteredAndSortedItems.map(item => ({
        id: item.id,
        type: 'text',
        source: item.source,
        target: item.target,
        label: item.type === 'block' ? t('profile.glossary.blockedLabel') : undefined,
        disabled: item.type === 'block',
        translationMode: item.translationMode,
        deletable: true,
        // no immediate "delete translation": clearing the field and saving does the same, with
        // the save as the step where the merchant can still reconsider
        translationDeletable: false,
        planRestricted: false
      }))
    }];

    // Only update if data actually changed (deep comparison of fields)
    setItemsListData(prevData => {
      if (prevData.length === 0 || prevData[0].fields.length !== newData[0].fields.length) {
        return newData;
      }

      // Compare each field to see if anything changed
      const hasChanges = newData[0].fields.some((newField, index) => {
        const oldField = prevData[0].fields[index];
        return !oldField ||
               oldField.id !== newField.id ||
               oldField.source !== newField.source ||
               oldField.target !== newField.target ||
               oldField.translationMode !== newField.translationMode;
      });

      return hasChanges ? newData : prevData;
    });
  }, [filteredAndSortedItems]);

  // Update overlimit ItemsList data whenever overlimit items change
  useEffect(() => {
    const newData = [{
      id: 'glossary-overlimit',
      title: t('profile.glossary.planRestrictedTitle'),
      fields: overlimitItems.map(item => ({
        id: item.id,
        type: 'text',
        source: item.source,
        target: item.target,
        label: item.type === 'block' ? t('profile.glossary.blockedLabel') : undefined,
        disabled: item.type === 'block',
        translationMode: item.translationMode,
        deletable: true,
        planRestricted: true
      }))
    }];

    // Only update if data actually changed (deep comparison of fields)
    setOverlimitItemsListData(prevData => {
      if (prevData.length === 0 || (newData[0].fields.length === 0 && prevData[0]?.fields.length === 0)) {
        return newData;
      }

      if (prevData.length === 0 || prevData[0].fields.length !== newData[0].fields.length) {
        return newData;
      }

      // Compare each field to see if anything changed
      const hasChanges = newData[0].fields.some((newField, index) => {
        const oldField = prevData[0].fields[index];
        return !oldField ||
               oldField.id !== newField.id ||
               oldField.source !== newField.source ||
               oldField.target !== newField.target ||
               oldField.translationMode !== newField.translationMode;
      });

      return hasChanges ? newData : prevData;
    });
  }, [overlimitItems]);

  const handleShowCreateForm = () => {
    setIsCreating(true);
    setCreateForm({ source: '', type: 'translate', target: '' });
    setCreateValidation({ source: false, target: false });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({ source: '', type: 'translate', target: '' });
    setCreateValidation({ source: false, sourceDuplicate: false, target: false });
  };

  const isDuplicateSource = useCallback((source) => {
    const trimmed = source.trim();
    if (!trimmed) return false;
    if (isCaseSensitive) {
      return glossaryItems.some(item => item.source.trim() === trimmed);
    }
    const lower = trimmed.toLowerCase();
    return glossaryItems.some(item => item.source.toLowerCase() === lower);
  }, [glossaryItems, isCaseSensitive]);

  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user types
    if (field === 'source') {
      setCreateValidation(prev => ({
        ...prev,
        source: false,
        sourceDuplicate: isDuplicateSource(value)
      }));
    } else if (createValidation[field]) {
      setCreateValidation(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitCreate = () => {
    // Validate
    const sourceEmpty = !createForm.source.trim();
    const sourceDuplicate = !sourceEmpty && isDuplicateSource(createForm.source);
    const targetEmpty = createForm.type === 'translate' && !createForm.target.trim();

    if (sourceEmpty || sourceDuplicate || targetEmpty) {
      setCreateValidation({
        source: sourceEmpty,
        sourceDuplicate: sourceDuplicate,
        target: targetEmpty
      });
      return;
    }

    // Submit
    setIsSubmitting(true);
    dispatch(createGlossaryEntry(
      createForm.source,
      createForm.type,
      createForm.target,
      editLanguage
    ));
  };


  // Counter to force ItemsList remount when translation mode changes
  const [remountKey, setRemountKey] = useState(0);

  const handleTranslationModeChange = useCallback((fieldId, newMode) => {
    const item = glossaryItems.find(i => i.id === fieldId);
    if (item) {
      // Optimistic update - update local state immediately
      setGlossaryItems(items =>
        items.map(i =>
          i.id === fieldId
            ? {
                ...i,
                type: newMode,
                translationMode: newMode,
                target: newMode === 'block' ? '' : i.target
              }
            : i
        )
      );

      // Force ItemsList to remount with new translation mode
      setRemountKey(prev => prev + 1);

      // Sync with server: the type only. The general update reads an empty target as "delete
      // the translation", which is not what a mode switch means - the translations are kept and
      // come back with the response when the term returns to translate mode
      dispatch(updateGlossaryEntryMode(
        fieldId,
        item.source,
        newMode,
        editLanguage
      ));
    }
  }, [glossaryItems, editLanguage, dispatch]);

  const handleDeleteItem = useCallback((id) => {
    dispatch(deleteGlossaryEntry(id, editLanguage));
  }, [dispatch, editLanguage]);

  const handleDeleteTranslation = useCallback((entryId) => {
    dispatch(deleteGlossaryTranslation(entryId, editLanguage));
  }, [dispatch, editLanguage]);

  const handleSave = useCallback((items) => {
    // Handle save for modified items
    if (items && items.length > 0 && items[0].fields) {
      items[0].fields.forEach(field => {
        // Find original item from glossaryItems (the source of truth)
        const originalItem = glossaryItems.find(i => i.id === field.id);
        if (originalItem && originalItem.target !== field.target) {
          dispatch(updateGlossaryEntry(
            field.id,
            field.source,
            originalItem.type,
            field.target,
            editLanguage
          ));
        }
      });
    }
  }, [glossaryItems, dispatch, editLanguage]);

  const handleGlossaryToggle = useCallback((event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      dispatch(enableLangifyFeature('glossary'));
    } else {
      dispatch(disableLangifyFeature('glossary'));
    }
  }, [dispatch]);

  // Mock history object to prevent unwanted navigation
  // The filter components use history.push() for deep-linking in metaobjects,
  // but glossary doesn't need this behavior
  const mockHistory = useMemo(() => ({
    push: () => {} // Do nothing - prevent redirect
  }), []);

  // Memoized filter handlers
  const handleQueryChange = useCallback((filterObject) => {
    if (filterObject.search) {
      // Extract search value from display_name field
      const searchValue = filterObject.search.display_name || '';
      setSearchQuery(searchValue);
    } else if (filterObject.search === undefined && filterObject.sort_key === undefined) {
      // Handle reset - when both are undefined, it's a reset
      setSearchQuery('');
    }
  }, []);

  const handleSortChange = useCallback((filterObject) => {
    if (filterObject.sort_key) {
      setSortOrder(filterObject.sort_key);
    }
  }, []);

  const handleTranslationModeFilterChange = useCallback((filterObject) => {
    if (filterObject.translation_mode !== undefined) {
      setTranslationModeFilter(filterObject.translation_mode);
    }
  }, []);

  // Filter query module component
  const FilterQueryModule = useMemo(() => (
    <FilterOptionsQuery
      initialItem={null}
      onChange={handleQueryChange}
      history={mockHistory}
    />
  ), [handleQueryChange, mockHistory]);

  // Filter translation mode module component
  const FilterTranslationModeModule = useMemo(() => (
    <FilterOptionsSelect
      options={[
        { type: 'all', name: t('profile.glossary.filters.allTypes') },
        { type: 'translate', name: t('profile.glossary.filters.translateOnly') },
        { type: 'block', name: t('profile.glossary.filters.blockedOnly') },
      ]}
      onChange={handleTranslationModeFilterChange}
      history={mockHistory}
      initialValue="all"
      showEmptyOption={false}
      filterKey="translation_mode"
    />
  ), [handleTranslationModeFilterChange, mockHistory, t]);

  // Filter sort module component
  const FilterSortModule = useMemo(() => (
    <FilterOptionsSort
      initialValue={sortOrder}
      options={[
        { type: 'empty_first', name: t('profile.glossary.sorts.emptyFirst') },
        { type: 'id', name: t('profile.glossary.sorts.newestFirst') },
        { type: 'alphabetical', name: t('profile.glossary.sorts.alphabetical') },
      ]}
      onChange={handleSortChange}
      history={mockHistory}
    />
  ), [sortOrder, handleSortChange, mockHistory, t]);

  return (
    <React.Fragment>
      <Grid container spacing={3} alignItems="flex-start" className="page-content">
        <Grid size={12} className="page-content">
          <Typography variant="h4" component="h2">
            {t('profile.glossary.title')}
            <Chip label="BETA" size="small" sx={glossaryStyles.betaBadge} />
            
            <SolutionsArticleLink
              article="glossary"
              subject={t('profile.glossary.title')}
              placement="heading"
            />
          </Typography>
          <Divider className="divider-white" />
        </Grid>
        <Grid size={12} sx={glossaryStyles.contentColumn}>
          <Paper sx={glossaryStyles.paper}>
            <Typography variant="h6" gutterBottom>
              {t('profile.glossary.subtitle')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <Trans i18nKey="profile.glossary.description" />
            </Typography>
            <Divider sx={glossaryStyles.divider} />

            {/* Enable/Disable Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={isGlossaryEnabled}
                  onChange={handleGlossaryToggle}
                  color="primary"
                />
              }
              label={isGlossaryEnabled ? t('system.enabled') : t('system.disabled')}
            />
            <Divider sx={glossaryStyles.divider} />

            {/* Case-Sensitive Toggle */}
            <Collapse in={isGlossaryEnabled}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isCaseSensitive}
                    onChange={handleCaseSensitiveToggle}
                    color="primary"
                    disabled={saveGlossarySettingsPending}
                  />
                }
                label={t(isCaseSensitive
                  ? 'profile.glossary.caseSensitiveOn'
                  : 'profile.glossary.caseSensitiveOff')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                {t('profile.glossary.caseSensitiveDescription')}
              </Typography>
              <Divider sx={glossaryStyles.divider} />
            </Collapse>


            {!isCreating ? (
              <Collapse in={isGlossaryEnabled} >
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      {t('profile.glossary.entriesCount')}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {glossaryItems.length} / {glossaryLimit}
                    </Typography>
                  </div>
                  {!isLimitReached ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleShowCreateForm}
                    >
                      {t('profile.glossary.addEntry')}
                    </Button>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={true}
                        style={{ paddingRight: 80 }}
                        startIcon={<AddIcon />}
                      >
                        {t('profile.glossary.addEntry')}
                      </Button>
                      {canUpgrade &&
                        <UpgradePlanHint
                          neededPlan={nextPlan}
                          position="end"
                          placement="bottom"
                          features={upgradeFeatures}
                          isOverlay
                          isBlock
                        />
                      }
                    </div>
                  )}
                </div>
              </Collapse>
            ) : (
              <Paper sx={glossaryStyles.createFormContainer} elevation={2}>
                <Box sx={glossaryStyles.createFormHeader}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" />
                    {t('profile.glossary.createNew')}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleCancelCreate}
                    disabled={isSubmitting}
                    sx={{ minWidth: 'auto', p: 0.5, color: 'text.secondary' }}
                  >
                    <CloseIcon fontSize="small" />
                  </Button>
                </Box>

                <Box sx={glossaryStyles.createFormBody}>
                  <Box sx={glossaryStyles.createFormFields}>
                    <TextField
                      select
                      sx={glossaryStyles.selectControl}
                      label={t('profile.glossary.type')}
                      value={createForm.type}
                      onChange={(e) => handleCreateFormChange('type', e.target.value)}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      // the Select inside takes the theme's standard default, which trims an outlined input
                      SelectProps={{ variant: 'outlined' }}
                      disabled={isSubmitting}
                    >
                      <MenuItem value="translate">{t('profile.glossary.typeTranslate')}</MenuItem>
                      <MenuItem value="block">{t('profile.glossary.typeBlock')}</MenuItem>
                    </TextField>

                    <TextField
                      sx={glossaryStyles.sourceField}
                      label={t('profile.glossary.source')}
                      value={createForm.source}
                      onChange={(e) => handleCreateFormChange('source', e.target.value)}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      error={createValidation.source || createValidation.sourceDuplicate}
                      helperText={
                        createValidation.source
                          ? t('profile.glossary.sourceRequired')
                          : createValidation.sourceDuplicate
                            ? t('profile.glossary.sourceDuplicate')
                            : ""
                      }
                      required
                      disabled={isSubmitting}
                    />

                    {createForm.type === 'translate' ? (
                      <>
                        <Box sx={glossaryStyles.createFormArrow}>
                          <ArrowForwardIcon fontSize="small" />
                        </Box>
                        <TextField
                          sx={glossaryStyles.targetField}
                          label={t('profile.glossary.target')}
                          value={createForm.target}
                          onChange={(e) => handleCreateFormChange('target', e.target.value)}
                          variant="outlined"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          error={createValidation.target}
                          helperText={createValidation.target ? t('profile.glossary.targetRequired') : ""}
                          required
                          disabled={isSubmitting}
                        />
                      </>
                    ) : (
                      <Box sx={glossaryStyles.createFormArrow}>
                        <BlockIcon fontSize="small" />
                      </Box>
                    )}
                  </Box>

                  <Box sx={glossaryStyles.createFormActions}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancelCreate}
                      disabled={isSubmitting}

                    >
                      {t('system.cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleSubmitCreate}
                      disabled={isSubmitting}

                    >
                      {isSubmitting ? t('profile.glossary.creating') : t('profile.glossary.createEntry')}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}
          </Paper>


          <Paper sx={glossaryStyles.paperList} style={{ opacity: !isGlossaryEnabled ? 0.5 : 1 }}>
            {/* Regular glossary entries with filtering */}
            <ItemsList
              key={`glossary-${remountKey}`}
              type="glossary"
              filterModules={[FilterQueryModule, FilterTranslationModeModule, FilterSortModule]}
              data={itemsListData}
              forceUpdate={filteredAndSortedItems.length}
              isPending={createPending}
              isRemoving={deletePending}
              isRemovingTranslation={deleteTranslationPending}
              isSaving={updatePending}
              showOnlyFieldsList={true}
              onDeleteItem={handleDeleteItem}
              onDeleteTranslation={handleDeleteTranslation}
              onSave={handleSave}
              onTranslationModeChange={handleTranslationModeChange}
              allowAutoTranslation={false}
              hideFilterbar={false}
              hideSorting={false}
              deletable={true}
              hideSourceToTargetMenu={true}
            />

            {!isGlossaryEnabled && <div className="plan-restricted-overlay"></div>}
          </Paper>

          {/* Overlimit entries (plan-restricted) without filtering */}
          {overlimitItems.length > 0 && canUpgrade && (
              <div style={{position: 'relative', marginTop: 16 }}>
                <ItemsList
                  key={`glossary-overlimit-${remountKey}`}
                  type="glossary"
                  filterModules={[]}
                  data={overlimitItemsListData}
                  forceUpdate={overlimitItems.length}
                  isPending={false}
                  isRemoving={deletePending}
                  isRemovingTranslation={deleteTranslationPending}
                  isSaving={updatePending}
                  showOnlyFieldsList={true}
                  onDeleteItem={handleDeleteItem}
                  onDeleteTranslation={handleDeleteTranslation}
                  onSave={handleSave}
                  onTranslationModeChange={handleTranslationModeChange}
                  allowAutoTranslation={false}
                  hideFilterbar={true}
                  hideSorting={true}
                  deletable={true}
                  hideSourceToTargetMenu={true}
                />


                <div className="plan-restricted-overlay"></div>
                <UpgradePlanHint neededPlan={nextPlan} position="center" placement="top" top={1} hideIcons={true} isOverlay features={upgradeFeatures} />
              </div>
          )}
        </Grid>
      </Grid>
      <LoadingIndicator
        pending={
          getPending ||
          createPending ||
          updatePending ||
          deletePending ||
          deleteTranslationPending ||
          exportPending ||
          importPending
        }
        loadingType={getLoadingType()}
        progress={progress}
        error={glossaryError}
        onErrorCancel={() => dispatch(resetErrors())}
        onErrorRetry={() => {
          // Retry the last failed operation
          dispatch(getGlossary(editLanguage));
        }}
      />
    </React.Fragment>
  );
}

export default withSnackbar(Glossary);